import { err, ok, type Result } from '../result.ts';
import { compareDays, daysBetween, parseIsoDay } from '../time.ts';

// Clinical documents: sick sheets, referral letters and uploaded documents
// (docs/plans/phase1-consultation.md §2.14–2.16, §5.6; decisions C19, S4, P10).
// Pure: no clock, no I/O.

// --- Uploaded documents (P10) ------------------------------------------------------------

/** The only types Wodi keeps. Known by their first bytes, never by the file name. */
export const DOCUMENT_CONTENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;
export type DocumentContentType = (typeof DOCUMENT_CONTENT_TYPES)[number];

/** 10 MB (P10). */
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

export const DOCUMENT_KINDS = [
  'external_result',
  'referral_in',
  'consent_form',
  'identity_document',
  'imaging_report',
  'other',
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = {
  external_result: 'Result from elsewhere',
  referral_in: 'Referral letter received',
  consent_form: 'Signed consent form',
  identity_document: 'Identity document',
  imaging_report: 'Imaging report',
  other: 'Other document',
};

const SIGNATURES: readonly { type: DocumentContentType; bytes: readonly number[] }[] = [
  // "%PDF-"
  { type: 'application/pdf', bytes: [0x25, 0x50, 0x44, 0x46, 0x2d] },
  { type: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
  { type: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
];

/** The type the bytes really are, from their magic bytes, or null when not an allowed one. */
export function sniffContentType(bytes: Uint8Array): DocumentContentType | null {
  for (const signature of SIGNATURES) {
    if (bytes.length < signature.bytes.length) continue;
    if (signature.bytes.every((b, i) => bytes[i] === b)) return signature.type;
  }
  return null;
}

export type DocumentCheckCode =
  'document.empty' | 'document.too_large' | 'document.type_not_allowed';

export const DOCUMENT_CHECK_MESSAGES: Record<DocumentCheckCode, string> = {
  'document.empty': 'That file is empty. Choose the scanned document again.',
  'document.too_large':
    'That file is larger than 10 MB. Scan it at a lower resolution and try again.',
  'document.type_not_allowed':
    'Wodi keeps PDF, JPEG and PNG files only. Save or scan the document as one of those.',
};

/** Whether an upload may be kept, and what it really is. */
export function checkDocumentBytes(
  bytes: Uint8Array,
): Result<{ contentType: DocumentContentType }, DocumentCheckCode> {
  if (bytes.length === 0) return err('document.empty');
  if (bytes.length > MAX_DOCUMENT_BYTES) return err('document.too_large');
  const contentType = sniffContentType(bytes);
  if (contentType === null) return err('document.type_not_allowed');
  return ok({ contentType });
}

// --- Sick sheets (C19) ------------------------------------------------------------------

export const SICK_SHEET_RECOMMENDATIONS = ['off_duty', 'light_duty'] as const;
export type SickSheetRecommendation = (typeof SICK_SHEET_RECOMMENDATIONS)[number];

export const SICK_SHEET_RECOMMENDATION_LABELS: Record<SickSheetRecommendation, string> = {
  off_duty: 'Off duty',
  light_duty: 'Light duty',
};

/**
 * The longest rest period a sick sheet may give, in days. None until the clinical lead
 * sets one. TODO(clinical-verify): C19.
 */
export const SICK_SHEET_MAX_DAYS: number | null = null;

/** Days of rest, counting both the first and the last day. */
export function sickSheetDays(from: string, to: string): number {
  const start = parseIsoDay(from);
  const end = parseIsoDay(to);
  if (!start.ok || !end.ok) throw new RangeError('sickSheetDays needs two ISO days');
  return daysBetween(start.value, end.value) + 1;
}

export type SickSheetDateCode =
  | 'sick_sheet.invalid_date'
  | 'sick_sheet.end_before_start'
  | 'sick_sheet.start_before_visit'
  | 'sick_sheet.too_long';

export const SICK_SHEET_DATE_MESSAGES: Record<SickSheetDateCode, string> = {
  'sick_sheet.invalid_date': 'Enter the rest dates as real calendar days.',
  'sick_sheet.end_before_start': 'The last day of rest is before the first. Check the dates.',
  'sick_sheet.start_before_visit':
    'Rest cannot start before the day of this visit. A sick sheet is not backdated.',
  'sick_sheet.too_long': 'That rest period is longer than this facility allows.',
};

/**
 * C19: rest starts on the visit's day (East Africa Time) or later; no backdating; no
 * maximum until the clinical lead sets one (TODO(clinical-verify)). `visitDay` is the EAT
 * day the visit started, as `2026-09-22`.
 */
export function checkSickSheetDates(input: {
  from: string;
  to: string;
  visitDay: string;
  maxDays?: number | null;
}): Result<{ days: number }, SickSheetDateCode> {
  const from = parseIsoDay(input.from);
  const to = parseIsoDay(input.to);
  const visit = parseIsoDay(input.visitDay);
  if (!from.ok || !to.ok || !visit.ok) return err('sick_sheet.invalid_date');
  if (compareDays(to.value, from.value) < 0) return err('sick_sheet.end_before_start');
  if (compareDays(from.value, visit.value) < 0) return err('sick_sheet.start_before_visit');
  const days = daysBetween(from.value, to.value) + 1;
  const max = input.maxDays === undefined ? SICK_SHEET_MAX_DAYS : input.maxDays;
  if (max !== null && days > max) return err('sick_sheet.too_long');
  return ok({ days });
}

// --- Referral letters (S4) --------------------------------------------------------------

export const REFERRAL_URGENCIES = ['routine', 'urgent', 'emergency'] as const;
export type ReferralUrgency = (typeof REFERRAL_URGENCIES)[number];

export const REFERRAL_URGENCY_LABELS: Record<ReferralUrgency, string> = {
  routine: 'Routine',
  urgent: 'Urgent',
  emergency: 'Emergency',
};

/** What the letter can carry, read from the visit when the clinician opens the letter. */
export interface ReferralSummaryInputs {
  diagnoses: readonly { display: string; code: string | null; rank: string }[];
  allergies: {
    state: 'allergies' | 'no_known_allergies' | 'not_recorded';
    items: readonly string[];
  };
  vitals: readonly { label: string; value: string }[];
  vitalsTakenAt: string | null;
  medicines: readonly { display: string; directions: string }[];
  results: readonly { id: string; text: string; releasedAt: string }[];
}

/** What the clinician chose to send. */
export interface ReferralSelection {
  resultIds: readonly string[];
  clinicianText: string;
}

export type ReferralSummary = Omit<ReferralSummaryInputs, 'results'> & {
  results: ReferralSummaryInputs['results'];
  clinicianText: string | null;
};

/**
 * The snapshot the letter stores at issue (plan §2.15): what the visit held when the
 * clinician issued it, with only the results they chose and their own words. It is never
 * re-read later. Results that are not in the inputs are ignored, so a letter can only carry
 * this patient's released results.
 */
export function referralSummary(
  inputs: ReferralSummaryInputs,
  selection: ReferralSelection,
): ReferralSummary {
  const chosen = new Set(selection.resultIds);
  const text = selection.clinicianText.trim();
  return {
    diagnoses: inputs.diagnoses,
    allergies: inputs.allergies,
    vitals: inputs.vitals,
    vitalsTakenAt: inputs.vitalsTakenAt,
    medicines: inputs.medicines,
    results: inputs.results.filter((r) => chosen.has(r.id)),
    clinicianText: text === '' ? null : text,
  };
}
